import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import {
    FiUsers, FiArrowLeft, FiPlay, FiCheckCircle,
    FiAlertTriangle, FiInfo, FiEye, FiEyeOff,
} from "react-icons/fi";
import userService from "../../services/userService";
import courseService from "../../services/courseService";
import { academicYearService } from "../../services/yearService";
import api from "../../services/api";
import { getYearOptions } from "../../utils/courseYears";

// ─── Constants ────────────────────────────────────────────────────────────────
const USERNAME_FIELDS = [
    { value: "enrollmentNo", label: "Enrollment No. (recommended)" },
    { value: "aadharNo", label: "Aadhaar No." },
    { value: "aaparId", label: "APAAR ID" },
];

// ─── Step indicator ───────────────────────────────────────────────────────────
function StepBadge({ n, label, active, done }) {
    return (
        <div style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
            <div style={{
                width: 26, height: 26, borderRadius: "50%",
                background: done ? "#16a34a" : active ? "#003b67" : "#e2e8f0",
                color: done || active ? "#fff" : "#94a3b8",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 12, fontWeight: 800, flexShrink: 0,
                transition: "background 0.2s",
            }}>
                {done ? <FiCheckCircle size={13} /> : n}
            </div>
            <span style={{ fontSize: 13, fontWeight: active ? 700 : 400, color: active ? "#003b67" : done ? "#16a34a" : "var(--text-muted)" }}>
                {label}
            </span>
        </div>
    );
}

// ─── Preview table ────────────────────────────────────────────────────────────
function PreviewTable({ students, usernameField }) {
    const missing = students.filter((s) => !s[usernameField]);
    const fieldLabel = usernameField === "enrollmentNo" ? "Enrollment No." : usernameField === "aadharNo" ? "Aadhaar No." : "APAAR ID";

    return (
        <div>
            {missing.length > 0 && (
                <div style={{ background: "#fef3c7", border: "1px solid #fcd34d", borderRadius: 8, padding: "0.65rem 1rem", marginBottom: "0.75rem", fontSize: 13, color: "#92400e", display: "flex", alignItems: "center", gap: 8 }}>
                    <FiAlertTriangle size={14} />
                    <strong>{missing.length}</strong> student{missing.length !== 1 ? "s" : ""} have no{" "}
                    {fieldLabel} and will be skipped.
                </div>
            )}

            <div className="table-wrapper" style={{ maxHeight: 320, overflowY: "auto" }}>
                <table style={{ fontSize: 12.5 }}>
                    <thead>
                        <tr>
                            <th>#</th>
                            <th>Name</th>
                            <th>Username ({fieldLabel})</th>
                            <th style={{ textAlign: "center" }}>Will Create?</th>
                        </tr>
                    </thead>
                    <tbody>
                        {students.map((s, i) => {
                            const username = s[usernameField];
                            const canCreate = Boolean(username);
                            return (
                                <tr key={s._id} style={{ background: !canCreate ? "#fff5f5" : "transparent" }}>
                                    <td style={{ color: "var(--text-muted)" }}>{i + 1}</td>
                                    <td style={{ fontWeight: 600 }}>{s.fullName}</td>
                                    <td>
                                        {username
                                            ? <code style={{ fontSize: 12, background: "#f1f5f9", padding: "1px 6px", borderRadius: 4 }}>{username.toLowerCase()}</code>
                                            : <span style={{ color: "#dc2626", fontStyle: "italic" }}>Missing</span>
                                        }
                                    </td>
                                    <td style={{ textAlign: "center" }}>
                                        {canCreate
                                            ? <span style={{ color: "#16a34a", fontSize: 13 }}>✓</span>
                                            : <span style={{ color: "#dc2626", fontSize: 13 }}>✗ Skip</span>
                                        }
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

// ─── Result panel ─────────────────────────────────────────────────────────────
function ResultPanel({ result, onReset }) {
    const [showErrors, setShowErrors] = useState(false);

    return (
        <div style={{ display: "grid", gap: "1rem" }}>
            {/* Summary */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "0.75rem" }}>
                {[
                    { label: "Created", value: result.created, color: "#16a34a", bg: "#f0fdf4" },
                    { label: "Skipped", value: result.skipped, color: "#d97706", bg: "#fefce8" },
                    { label: "Errors", value: result.errors?.length ?? 0, color: "#dc2626", bg: "#fff5f5" },
                ].map(({ label, value, color, bg }) => (
                    <div key={label} style={{ background: bg, border: `1px solid ${color}30`, borderRadius: 8, padding: "1rem", textAlign: "center" }}>
                        <div style={{ fontSize: 32, fontWeight: 900, color, lineHeight: 1 }}>{value}</div>
                        <div style={{ fontSize: 13, color: "var(--text-muted)", marginTop: 4 }}>{label}</div>
                    </div>
                ))}
            </div>

            {/* Info */}
            <div style={{ background: "#e8f1f8", border: "1px solid #b6d5ec", borderRadius: 8, padding: "0.85rem 1rem", fontSize: 13, color: "#002747" }}>
                <FiInfo size={13} style={{ verticalAlign: "middle", marginRight: 6 }} />
                All created accounts have <strong>mustChangePassword = true</strong>. Students will be prompted to set their own password on first login.
            </div>

            {/* Errors */}
            {result.errors?.length > 0 && (
                <div>
                    <button
                        className="btn btn-outline btn-sm"
                        onClick={() => setShowErrors((p) => !p)}
                        style={{ display: "flex", alignItems: "center", gap: "0.35rem", marginBottom: "0.5rem" }}
                    >
                        <FiAlertTriangle size={12} color="#dc2626" />
                        {showErrors ? "Hide" : "Show"} {result.errors.length} error{result.errors.length !== 1 ? "s" : ""}
                    </button>
                    {showErrors && (
                        <div style={{ background: "#fff5f5", border: "1px solid #fca5a5", borderRadius: 8, padding: "0.75rem 1rem" }}>
                            {result.errors.map((e, i) => (
                                <div key={i} style={{ fontSize: 12.5, color: "#dc2626", padding: "2px 0" }}>
                                    ✗ {e.name} — {e.reason}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* Actions */}
            <div style={{ display: "flex", gap: "0.5rem" }}>
                <button className="btn btn-outline" onClick={onReset}>Create Another Batch</button>
                <button className="btn btn-primary" onClick={() => window.location.href = "/users"}>
                    <FiUsers size={13} /> View All Users
                </button>
            </div>
        </div>
    );
}

// ─── Main ─────────────────────────────────────────────────────────────────────
export default function BulkStudents() {
    const navigate = useNavigate();

    // Step state: 1=configure, 2=preview, 3=done
    const [step, setStep] = useState(1);

    const [academicYears, setAcademicYears] = useState([]);
    const [form, setForm] = useState({
        academicYearId: "",
        course: "",
        yearNumber: "",
        usernameField: "enrollmentNo",
        defaultPassword: "",
    });
    const [showPw, setShowPw] = useState(false);
    const [courses, setCourses] = useState([]);
    const [students, setStudents] = useState([]);   // fetched for preview
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);
    const [errors, setErrors] = useState({});

    useEffect(() => {
        academicYearService.getAll()
            .then((r) => setAcademicYears(r.data))
            .catch(() => { });
    }, []);

    useEffect(() => {
        courseService
            .getAll({ isActive: true })
            .then((res) => setCourses(res.data || []))
            .catch((err) => toast.error("Couldn't load courses: " + err.message));
    }, []);

    const selectedCourse = useMemo(() => courses.find((c) => c._id === form.course), [courses, form.course]);

    const yearOptions = useMemo(() => {
        return selectedCourse ? getYearOptions(selectedCourse) : [];
    }, [selectedCourse]);

    const set = (field, val) => {
        setForm((p) => ({
            ...p,
            [field]: val,
            ...(field === "course" ? { yearNumber: "" } : {}),
        }));
        setErrors((p) => ({ ...p, [field]: "" }));
    };

    // ── Step 1 validate ───────────────────────────────────────────────────────
    const validateStep1 = () => {
        const errs = {};
        if (!form.academicYearId) errs.academicYearId = "Select an academic year.";
        if (!form.course) errs.course = "Select a course.";
        if (!form.yearNumber) errs.yearNumber = "Select a year.";
        if (!form.defaultPassword) errs.defaultPassword = "Default password is required.";
        else if (form.defaultPassword.length < 6)
            errs.defaultPassword = "Password must be at least 6 characters.";
        setErrors(errs);
        return Object.keys(errs).length === 0;
    };

    // ── Fetch students for preview ────────────────────────────────────────────
    const handlePreview = async () => {
        if (!validateStep1()) return;
        setLoading(true);
        try {
            const res = await api.get("/students", {
                params: {
                    academicYear: form.academicYearId,
                    course: form.course,
                    yearNumber: form.yearNumber,
                    status: "Active",
                    limit: 500,
                },
            });
            const list = res.data.data || res.data;
            if (!list.length) {
                toast.error("No active students found for this class.");
                return;
            }
            setStudents(list);
            setStep(2);
        } catch (err) {
            toast.error(err.message);
        } finally {
            setLoading(false);
        }
    };

    // ── Execute bulk create ───────────────────────────────────────────────────
    const handleCreate = async () => {
        if (!window.confirm(
            `Create accounts for ${students.filter((s) => s[form.usernameField]).length} student(s)?\n\nAll accounts will use "${form.defaultPassword}" as the default password. Students must change it on first login.`
        )) return;

        setLoading(true);
        try {
            const res = await userService.bulkStudents({
                academicYearId: form.academicYearId,
                course: form.course,
                yearNumber: Number(form.yearNumber),
                usernameField: form.usernameField,
                defaultPassword: form.defaultPassword,
            });
            toast.success(res.message);
            setResult(res.data);
            setStep(3);
        } catch (err) {
            toast.error(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleReset = () => {
        setStep(1);
        setStudents([]);
        setResult(null);
        setForm((p) => ({ ...p, academicYearId: "", course: "", yearNumber: "" }));
    };

    const eligibleCount = students.filter((s) => s[form.usernameField]).length;
    const ayLabel = academicYears.find((y) => y._id === form.academicYearId)?.label || "";

    return (
        <div>
            {/* ── Header ── */}
            <div className="page-header" style={{ flexWrap: "wrap", gap: "0.5rem" }}>
                <div>
                    <h1 className="page-title">Bulk Student Accounts</h1>
                    <p className="page-subtitle">Create login accounts for an entire class in one shot</p>
                </div>
                <button className="btn btn-outline" onClick={() => navigate("/users")}
                    style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
                    <FiArrowLeft size={14} /> Back to Users
                </button>
            </div>

            {/* ── Step indicators ── */}
            <div style={{ display: "flex", gap: "1.5rem", marginBottom: "1.5rem", flexWrap: "wrap" }}>
                <StepBadge n={1} label="Configure" active={step === 1} done={step > 1} />
                <div style={{ width: 24, height: 1, background: "var(--border)", alignSelf: "center" }} />
                <StepBadge n={2} label="Preview" active={step === 2} done={step > 2} />
                <div style={{ width: 24, height: 1, background: "var(--border)", alignSelf: "center" }} />
                <StepBadge n={3} label="Done" active={step === 3} done={false} />
            </div>

            {/* ══════════════════════════════════════════════════════════════════════
          STEP 1 — Configure
      ══════════════════════════════════════════════════════════════════════ */}
            {step === 1 && (
                <div className="card" style={{ maxWidth: 560 }}>
                    <div className="card-header" style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
                        <FiUsers size={13} /> Class Configuration
                    </div>
                    <div className="card-body">
                        <div style={{ background: "#e8f1f8", border: "1px solid #b6d5ec", borderRadius: 8, padding: "0.75rem 1rem", marginBottom: "1.25rem", fontSize: 13, color: "#002747", lineHeight: 1.5 }}>
                            <FiInfo size={13} style={{ verticalAlign: "middle", marginRight: 6 }} />
                            Accounts are created for all <strong>Active</strong> students in the selected class.
                            Existing accounts are automatically skipped — no duplicates created.
                        </div>

                        <div className="form-grid">
                            {/* Academic year */}
                            <div className="form-group" style={{ gridColumn: "1 / -1" }}>
                                <label className="form-label">Academic Year <span style={{ color: "var(--danger)" }}>*</span></label>
                                <select
                                    className={`form-control ${errors.academicYearId ? "error" : ""}`}
                                    value={form.academicYearId}
                                    onChange={(e) => set("academicYearId", e.target.value)}
                                >
                                    <option value="">— Select academic year —</option>
                                    {academicYears.map((y) => (
                                        <option key={y._id} value={y._id}>
                                            {y.label}{y.isActive ? " ✓" : ""}
                                        </option>
                                    ))}
                                </select>
                                {errors.academicYearId && <span className="form-error">{errors.academicYearId}</span>}
                            </div>

                            {/* Course */}
                            <div className="form-group">
                                <label className="form-label">Course <span style={{ color: "var(--danger)" }}>*</span></label>
                                <select
                                    className={`form-control ${errors.course ? "error" : ""}`}
                                    value={form.course}
                                    onChange={(e) => set("course", e.target.value)}
                                >
                                    <option value="">Select course…</option>
                                    {courses.map((c) => <option key={c._id} value={c._id}>{c.shortName}</option>)}
                                </select>
                                {errors.course && <span className="form-error">{errors.course}</span>}
                            </div>

                            {/* Year */}
                            <div className="form-group">
                                <label className="form-label">Course Year <span style={{ color: "var(--danger)" }}>*</span></label>
                                <select
                                    className={`form-control ${errors.yearNumber ? "error" : ""}`}
                                    value={form.yearNumber}
                                    onChange={(e) => set("yearNumber", e.target.value)}
                                    disabled={!form.course}
                                >
                                    <option value="">Select year…</option>
                                    {yearOptions.map((y) => <option key={y.yearNumber} value={y.yearNumber}>{y.label}</option>)}
                                </select>
                                {errors.yearNumber && <span className="form-error">{errors.yearNumber}</span>}
                            </div>

                            {/* Username field */}
                            <div className="form-group" style={{ gridColumn: "1 / -1" }}>
                                <label className="form-label">Use as Username</label>
                                <div style={{ display: "flex", gap: "0.65rem" }}>
                                    {USERNAME_FIELDS.map(({ value, label }) => (
                                        <label
                                            key={value}
                                            style={{ display: "flex", alignItems: "center", gap: "0.4rem", fontSize: 13, cursor: "pointer", flex: 1, padding: "0.6rem 0.85rem", border: `2px solid ${form.usernameField === value ? "#003b67" : "var(--border)"}`, borderRadius: 8, background: form.usernameField === value ? "#e8f1f8" : "#fff", transition: "all 0.15s" }}
                                        >
                                            <input
                                                type="radio"
                                                name="usernameField"
                                                value={value}
                                                checked={form.usernameField === value}
                                                onChange={() => set("usernameField", value)}
                                            />
                                            {label}
                                        </label>
                                    ))}
                                </div>
                                <span className="form-hint">Usernames will be lowercased automatically.</span>
                            </div>

                            {/* Default password */}
                            <div className="form-group" style={{ gridColumn: "1 / -1" }}>
                                <label className="form-label">Default Password <span style={{ color: "var(--danger)" }}>*</span></label>
                                <div style={{ position: "relative" }}>
                                    <input
                                        type={showPw ? "text" : "password"}
                                        className={`form-control ${errors.defaultPassword ? "error" : ""}`}
                                        value={form.defaultPassword}
                                        onChange={(e) => set("defaultPassword", e.target.value)}
                                        placeholder="Min. 6 characters (e.g. Student@123)"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPw((p) => !p)}
                                        style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "var(--text-muted)", padding: 4 }}
                                    >
                                        {showPw ? <FiEyeOff size={14} /> : <FiEye size={14} />}
                                    </button>
                                </div>
                                {errors.defaultPassword && <span className="form-error">{errors.defaultPassword}</span>}
                                <span className="form-hint">Students will be forced to change this on their first login.</span>
                            </div>
                        </div>

                        <button
                            type="button"
                            className="btn btn-primary"
                            onClick={handlePreview}
                            disabled={loading}
                            style={{ marginTop: "0.5rem", display: "flex", alignItems: "center", gap: "0.4rem" }}
                        >
                            {loading ? "Loading students…" : <><FiPlay size={13} /> Preview Students</>}
                        </button>
                    </div>
                </div>
            )}

            {/* ══════════════════════════════════════════════════════════════════════
          STEP 2 — Preview
      ══════════════════════════════════════════════════════════════════════ */}
            {step === 2 && (
                <div className="card" style={{ maxWidth: 720 }}>
                    <div className="card-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
                            <FiUsers size={13} />
                            Preview — {selectedCourse?.shortName || "Course"} {form.yearNumber ? `Year ${form.yearNumber}` : ""} · {ayLabel}
                        </div>
                        <button className="btn btn-outline btn-sm" onClick={() => setStep(1)}>← Back</button>
                    </div>
                    <div className="card-body">
                        {/* Summary bar */}
                        <div style={{ display: "flex", gap: "1.25rem", marginBottom: "1rem", flexWrap: "wrap", fontSize: 13 }}>
                            <span>Total found: <strong>{students.length}</strong></span>
                            <span style={{ color: "#16a34a" }}>Will create: <strong>{eligibleCount}</strong></span>
                            <span style={{ color: "#d97706" }}>Will skip (missing field): <strong>{students.length - eligibleCount}</strong></span>
                            <span style={{ color: "var(--text-muted)" }}>Password: <code style={{ background: "#f1f5f9", padding: "1px 5px", borderRadius: 4, fontSize: 12 }}>{form.defaultPassword}</code></span>
                        </div>

                        <PreviewTable students={students} usernameField={form.usernameField} />

                        <div style={{ display: "flex", gap: "0.5rem", marginTop: "1.25rem" }}>
                            <button className="btn btn-outline" onClick={() => setStep(1)}>
                                ← Change Settings
                            </button>
                            <button
                                className="btn btn-primary"
                                onClick={handleCreate}
                                disabled={loading || eligibleCount === 0}
                                style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}
                            >
                                {loading ? "Creating accounts…" : <><FiCheckCircle size={13} /> Create {eligibleCount} Account{eligibleCount !== 1 ? "s" : ""}</>}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ══════════════════════════════════════════════════════════════════════
          STEP 3 — Done
      ══════════════════════════════════════════════════════════════════════ */}
            {step === 3 && result && (
                <div className="card" style={{ maxWidth: 560 }}>
                    <div className="card-header" style={{ display: "flex", alignItems: "center", gap: "0.4rem", color: "#16a34a" }}>
                        <FiCheckCircle size={15} /> Accounts Created
                    </div>
                    <div className="card-body">
                        <ResultPanel result={result} onReset={handleReset} />
                    </div>
                </div>
            )}
        </div>
    );
}
